import { useState } from 'react';
import { LifeBuoy , MessageCircle, Clock, CheckCircle, User } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import ReusableTable from '../../components/Tableau';

const SupportSupplierPage = ({ lang }) => {
  const { t } = useTranslation(lang);

  const [tickets, setTickets] = useState([
    {
      id: 101,
      subject: "Problème de paiement",
      requester: "admin@hotelidh.com",
      status: "open",
      category: "Payment",
      department: "Finance",
      createdAt: "2024-12-19T09:15:00Z",
      updatedAt: "2024-12-19T13:20:00Z",
      lastMessage: "Le paiement pour la réservation #1234 n'a pas été reçu..."
    },
    {
      id: 102,
      subject: "Question sur les tarifs",
      requester: "manager@resort.com",
      status: "pending",
      category: "Pricing",
      department: "Sales",
      createdAt: "2024-12-18T14:30:00Z",
      updatedAt: "2024-12-19T10:45:00Z",
      lastMessage: "Pouvez-vous m'expliquer la structure tarifaire pour..."
    },
    {
      id: 103,
      subject: "Problème technique",
      requester: "tech@travelagency.com",
      status: "open",
      category: "Technical",
      department: "Support",
      createdAt: "2024-12-19T11:00:00Z",
      updatedAt: "2024-12-19T15:30:00Z",
      lastMessage: "L'API de réservation ne fonctionne pas correctement..."
    },
    {
      id: 104,
      subject: "Demande de partenariat",
      requester: "partner@newhotel.com",
      status: "closed",
      category: "Partnership",
      department: "Business Development",
      createdAt: "2024-12-17T16:45:00Z",
      updatedAt: "2024-12-18T11:20:00Z",
      lastMessage: "Nous souhaitons devenir partenaire de votre plateforme..."
    }
  ]);

  // Statistiques
  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(ticket => ticket.status === 'open').length,
    closed: tickets.filter(ticket => ticket.status === 'closed').length,
    pending: tickets.filter(ticket => ticket.status === 'pending').length
  };

  const stats = [
    {
      title: t('totalTickets'),
      value: ticketStats.total.toString(),
      description: t('allTickets'),
      icon: LifeBuoy ,
      color: '#7c3aed',
      change: '+8%',
      isPositive: true
    },
    {
      title: t('openTickets'),
      value: ticketStats.open.toString(),
      description: t('needsAttention'),
      icon: MessageCircle,
      color: '#ef4444',
      change: '+3%',
      isPositive: false
    },
    {
      title: t('closedTickets'),
      value: ticketStats.closed.toString(),
      description: t('resolvedTickets'),
      icon: CheckCircle,
      color: '#10b981',
      change: '+12%',
      isPositive: true
    },
    {
      title: t('pendingTickets'),
      value: ticketStats.pending.toString(),
      description: t('waitingCustomer'),
      icon: Clock,
      color: '#f59e0b',
      change: '+2%',
      isPositive: false
    }
  ];

  // Colonnes pour le tableau
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'subject', label: t('subject') },
    { key: 'requester', label: t('customer') },
    { 
      key: 'status', 
      label: t('status'),
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: getStatusColor(value),
          color: 'white',
          textTransform: 'uppercase'
        }}>
          {t(value)}
        </span>
      )
    },
    { key: 'category', label: t('category') },
    { key: 'department', label: t('department') },
    { 
      key: 'createdAt', 
      label: t('createdAt'),
      render: (value) => formatDate(value)
    }
  ];


  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'closed': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleDetails = (ticket) => {
    console.log('Détails du ticket:', ticket);
    // Navigation vers la page de détails du ticket
  };

  const handleRowClick = (ticket) => {
    handleDetails(ticket);
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    statIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '4px'
    },
    statTitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px',
      fontWeight: '600'
    },
    statDescription: {
      fontSize: '12px',
      color: '#9ca3af'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <User size={32} color="#7c3aed" />
          <h1 style={styles.title}>{t('supportSupplier')}</h1>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div style={styles.statsGrid}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              style={styles.statCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{
                ...styles.statIcon,
                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`
              }}>
                <Icon size={28} color="white" />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statTitle}>{stat.title}</div>
                <div style={styles.statDescription}>{stat.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tableau des tickets */}
      <ReusableTable
        data={tickets}
        columns={columns}
        onDetails={handleDetails}
        onRowClick={handleRowClick}
        lang={lang}
        itemsPerPageOptions={[10, 20, 50]}
        colors={{
          headerBg: '#7c3aed',
          buttonDetails: '#10b981'
        }}
        showEditButton={false}
        showDeleteButton={false}
      />
    </div>
  );
};

export default SupportSupplierPage;